import { type ReactNode } from 'react';
import type { RichTextField } from '@prismicio/types';

import type { PrismicImage } from '@virginexperiencedays/cms';
import { cn } from '@virginexperiencedays/tailwind';

import { Image } from '../../../layout/Image';
import { VisualHeading } from '../../../typography/VisualHeading';
import { BodyText, type BodyTextProps } from '../../../typography/BodyText';

import { RichTextWrap } from '../../../typography/RichTextWrap';
import { StyledRichText } from '../../../typography/RichTextWrap/StyledRichText';

import { generateMultipleParagraphs } from '../../../utils/generateMultipleParagraphs';

export type PostHeroPanelProps = {
  title: string;
  className?: string;
  headingClassName?: string;
  location_title?: string;
  imgUrl?: string;
  imgAlt?: string;
  imageObject?: PrismicImage;
  description?: string | ReactNode | null;
  subDescription?: RichTextField | string[];
  subDescriptionLeading?: BodyTextProps['leading'];
  subDescriptionClassName?: string;
  /**
   * @param string Define max width for subdescription
   */
  descriptionMaxWidth?: string;
};
export const PostHeroPanel = ({
  title,
  imgUrl,
  imgAlt,
  description,
  subDescription,
  imageObject,
  className,
  headingClassName,
  subDescriptionClassName, // replacement for description max-width, max-w can be defined per usage instead
  subDescriptionLeading = 'relaxed',
}: PostHeroPanelProps) => {
  const isSubDescriptionRichTextField = isRichTextField(subDescription);

  return (
    <div className={cn('flex flex-col', className)}>
      {imgUrl && (
        <Image
          className="image:rounded-lg"
          dataTestId="hero-image"
          src={imgUrl}
          imageObject={imageObject}
          width={imageObject?.dimensions?.width ?? 1128}
          height={imageObject?.dimensions?.height ?? 400}
          priority
          alt={imgAlt}
        />
      )}
      <div className={cn(imgUrl ? 'max-w-[688px]' : 'max-w-none')}>
        <VisualHeading
          className={cn(
            'text-neutral-strong mb-6 mt-10 leading-tight tracking-tight sm:leading-tight',
            headingClassName
          )}
          size="2"
          as="h1"
          data-testid="hero-heading"
        >
          {title}
        </VisualHeading>
        {description && (
          <BodyText className="text-neutral my-6" size="large" data-testid="hero-description">
            {description}
          </BodyText>
        )}
        {subDescription && (
          <BodyText
            as={isSubDescriptionRichTextField ? 'div' : 'p'}
            className={cn('text-neutral', subDescriptionClassName)}
            leading={subDescriptionLeading}
            dataTestId="hero-sub-description"
          >
            {isSubDescriptionRichTextField ? (
              <StyledRichText>
                <RichTextWrap field={subDescription} />
              </StyledRichText>
            ) : (
              <>
                {subDescription.length > 1
                  ? generateMultipleParagraphs(subDescription)
                  : subDescription}
              </>
            )}
          </BodyText>
        )}
      </div>
    </div>
  );
};

/**
 * Checks if the provided value is a RichTextField.
 * @param v - The value to check.
 * @returns A boolean indicating whether the value is a RichTextField or not.
 */
export const isRichTextField = (v?: RichTextField | string[]): v is RichTextField =>
  !!v && v.length > 0 && typeof v[0] === 'object' && 'type' in v[0];
